import React, { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { Sparkles, Loader2, Lock, IdCard, AlertTriangle, UserCircle, X, RotateCcw } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";

/**
 * DoxxCardSection — one-time Classified personal Vault Card.
 *
 * Lets the signed-in user type a free-form description and mints a unique
 * AI-illustrated card with their username on it. The mint is permanent and
 * one-per-account. Includes a toggle to set the card image as their
 * profile avatar.
 */
export default function DoxxCardSection({ onAvatarChanged, compact = false }) {
  const { user, refreshUser } = useAuth();
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const mintingRef = useRef(false);
  const pollTimer = useRef(null);
  const [confirmed, setConfirmed] = useState(false);

  const reload = async () => {
    try {
      const r = await api.get("/profile/doxx-card");
      const next = r.data.card;
      setCard(next);
      if (mintingRef.current) {
        if (next && next.status === "ready") {
          mintingRef.current = false;
          toast.success(`Doxx Card minted · edition #${next.edition_number}`);
          refreshUser?.();
        } else if (!next) {
          mintingRef.current = false;
          toast.error("Mint failed — try again");
        }
      }
      if (pollTimer.current) { clearTimeout(pollTimer.current); pollTimer.current = null; }
      if (next?.status === "pending") pollTimer.current = setTimeout(reload, 3000);
      return next;
    } catch {
      setCard(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    return () => { if (pollTimer.current) clearTimeout(pollTimer.current); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onMint = async () => {
    if (submitting) return;
    if (prompt.trim().length < 4) {
      toast.error("Describe yourself in a few more words first");
      return;
    }
    if (!confirmed) {
      toast.error("Confirm you understand this is permanent");
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post("/profile/doxx-card/mint", { prompt: prompt.trim() });
      setCard(r.data.card);
      setOpen(false);
      if (r.data?.pending) {
        mintingRef.current = true;
        toast.success("Minting your Doxx Card… the portrait is rendering (up to a minute)");
        if (pollTimer.current) clearTimeout(pollTimer.current);
        pollTimer.current = setTimeout(reload, 3000);
      } else {
        toast.success(`Doxx Card minted · edition #${r.data.card.edition_number}`);
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Mint failed — try again";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const onUseAsAvatar = async () => {
    try {
      await api.post("/profile/doxx-card/use-as-avatar");
      toast.success("Doxx Card set as your avatar");
      refreshUser?.();
      onAvatarChanged?.("doxx");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't set avatar");
    }
  };

  const onClearAvatar = async () => {
    try {
      await api.post("/profile/doxx-card/clear-avatar");
      toast.success("Avatar cleared — using default");
      refreshUser?.();
      onAvatarChanged?.(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't clear avatar");
    }
  };

  const onReset = async () => {
    if (!window.confirm("Reset your Doxx Card? This permanently deletes the current card and lets you mint a new one. There's no undo.")) return;
    try {
      await api.delete("/profile/doxx-card");
      toast.success("Doxx Card reset — you can mint a new one");
      setCard(null);
      refreshUser?.();
      onAvatarChanged?.(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed");
    }
  };

  if (loading) return null;
  const isAvatar = user?.avatar_id === "doxx";

  // Card art rendering in the background — show a generating panel that polls.
  if (card && card.status === "pending") {
    return (
      <section data-testid="doxx-card-section-pending" className="border border-[#ff3b30]/40 bg-[#0a0a0a] p-5">
        <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.3em] text-[#ff3b30] mb-2">
          <Loader2 size={12} className="animate-spin" /> CLASSIFIED · MINTING
        </div>
        <h3 className="font-heading uppercase tracking-tight text-white text-xl mb-2">Painting your Doxx Card</h3>
        <p className="text-zinc-400 text-sm leading-relaxed">Nano Banana is rendering your brutalist portrait — this can take up to a minute. You can leave this page; it will be here when you return.</p>
      </section>
    );
  }

  // No card yet — show the mint CTA.
  if (!card) {
    return (
      <>
        <section
          data-testid="doxx-card-section-empty"
          className="border border-[#ff3b30]/30 bg-[#0a0a0a] p-5 relative overflow-hidden flex flex-col"
        >
          <div className="absolute -right-6 -top-6 w-32 h-32 rounded-full border border-[#ff3b30]/20 opacity-50 pointer-events-none" />
          <div className="absolute -right-10 -top-10 w-44 h-44 rounded-full border border-[#ff3b30]/15 opacity-30 pointer-events-none" />

          <div className="relative flex-1 flex flex-col">
            <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.3em] text-[#ff3b30] mb-2">
              <Lock size={12} /> CLASSIFIED · ONE-TIME MINT
            </div>
            <h3 className="font-heading uppercase tracking-tight text-white text-xl mb-2">
              Your Doxx Card
            </h3>
            <p className="text-zinc-400 text-sm leading-relaxed mb-4 flex-1">
              Mint a single, permanent Classified card just for you. You type
              the description — Gemini Nano Banana paints the brutalist portrait
              and it doubles as your profile avatar.
              <span className="block mt-1 text-[#ff3b30] text-xs">
                One mint per account. Forever.
              </span>
            </p>
            <button
              onClick={() => setOpen(true)}
              data-testid="doxx-card-open-mint"
              className="self-start inline-flex items-center gap-2 px-4 py-2 bg-[#ff3b30] hover:bg-white text-white hover:text-[#050505] font-mono text-[10px] tracking-[0.3em] uppercase transition-colors"
            >
              <IdCard size={12} /> Mint your card
            </button>
          </div>
        </section>

        {open && (
          <MintModal
            prompt={prompt}
            setPrompt={setPrompt}
            confirmed={confirmed}
            setConfirmed={setConfirmed}
            submitting={submitting}
            onClose={() => setOpen(false)}
            onSubmit={onMint}
            username={user?.username}
          />
        )}
      </>
    );
  }

  // User has a card. Compact mode = side-by-side row layout; full mode = grid.
  if (compact) {
    return (
      <section
        data-testid="doxx-card-section"
        className="border border-[#ff3b30]/30 bg-[#0a0a0a] p-4 flex gap-4"
      >
        <div className="w-[110px] shrink-0">
          <DoxxCardArt card={card} />
        </div>
        <div className="flex-1 min-w-0 flex flex-col">
          <div className="font-mono text-[9px] tracking-[0.3em] text-[#ff3b30] mb-1">
            <Lock size={10} className="inline mr-1 -mt-0.5" />
            CLASSIFIED · #{card.edition_number}
          </div>
          <div className="font-heading uppercase tracking-tight text-white text-lg leading-none mb-2 truncate">
            {card.username}
          </div>
          <p className="text-zinc-500 text-xs leading-relaxed italic mb-3 line-clamp-3">
            &ldquo;{card.prompt}&rdquo;
          </p>
          <div className="mt-auto flex flex-col gap-1.5">
            {!isAvatar ? (
              <button
                onClick={onUseAsAvatar}
                data-testid="doxx-use-as-avatar"
                className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 bg-[#ff3b30] hover:bg-white text-white hover:text-[#050505] font-mono text-[9px] tracking-[0.25em] uppercase transition-colors"
              >
                <UserCircle size={11} /> Use as avatar
              </button>
            ) : (
              <span className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 border border-[#34d399]/40 bg-[#34d399]/10 text-[#34d399] font-mono text-[9px] tracking-[0.25em] uppercase">
                ✓ Active avatar
              </span>
            )}
            {/* "Reset & re-mint" removed 2026-02 — minted assets are
                permanent by design. The doxx card is a one-shot identity
                artifact and re-minting cheapens it. */}
          </div>
        </div>
      </section>
    );
  }

  // Full layout (legacy)
  return (
    <section
      data-testid="doxx-card-section"
      className="border border-[#ff3b30]/30 bg-[#0a0a0a] p-5 sm:p-6"
    >
      <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.3em] text-[#ff3b30] mb-4">
        <Lock size={12} /> CLASSIFIED · EDITION #{card.edition_number}
      </div>

      <div className="grid md:grid-cols-[280px_1fr] gap-6">
        <DoxxCardArt card={card} />
        <div className="flex flex-col">
          <div className="font-mono text-[10px] tracking-[0.25em] text-zinc-500 mb-1">// HANDLE</div>
          <div className="font-heading uppercase tracking-tight text-white text-2xl mb-3">
            {card.username}
          </div>
          <div className="font-mono text-[10px] tracking-[0.25em] text-zinc-500 mb-1">// PROMPT</div>
          <p className="text-zinc-400 text-sm leading-relaxed italic mb-4 max-w-xl">
            &ldquo;{card.prompt}&rdquo;
          </p>
          <div className="font-mono text-[10px] tracking-[0.18em] text-zinc-500 mb-5">
            Minted {card.minted_at ? new Date(card.minted_at).toLocaleDateString() : "—"}
          </div>

          <div className="flex flex-wrap gap-2 mt-auto">
            {!isAvatar ? (
              <button
                onClick={onUseAsAvatar}
                data-testid="doxx-use-as-avatar"
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#ff3b30] hover:bg-white text-white hover:text-[#050505] font-mono text-[11px] tracking-[0.25em] uppercase transition-colors"
              >
                <UserCircle size={13} /> Use as my avatar
              </button>
            ) : (
              <>
                <span className="inline-flex items-center gap-2 px-4 py-2 border border-[#34d399]/40 bg-[#34d399]/10 text-[#34d399] font-mono text-[11px] tracking-[0.25em] uppercase">
                  ✓ Active avatar
                </span>
                <button
                  onClick={onClearAvatar}
                  data-testid="doxx-clear-avatar"
                  className="inline-flex items-center gap-2 px-4 py-2 border border-white/15 hover:border-white/30 text-zinc-300 font-mono text-[11px] tracking-[0.25em] uppercase transition-colors"
                >
                  Use default avatar instead
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function DoxxCardArt({ card }) {
  const url = `${process.env.REACT_APP_BACKEND_URL}/api${card.art_url.replace(/^\/api/, "")}`;
  return (
    <div
      // Doxx Card is the master of all cards — STACK every rarity overlay:
      // Classified shimmer + glow, Divine ripple, Celestial prism shards,
      // Mythic starfield, and Rare shine — all at once.
      className="vc-classified-shimmer vc-classified-glow vc-divine-shimmer vc-divine-glow vc-celestial-prism vc-celestial-glow vc-mythic-glow vc-rare-shine vc-rare-glow vc-doxx-aura relative aspect-[3/4] border-2 border-[#ff3b30] bg-[#0F0F0F] overflow-hidden"
      data-testid="doxx-card-art"
      data-rarity="classified"
      style={{ boxShadow: "0 0 60px rgba(255,59,48,0.35), 0 0 120px rgba(167,139,250,0.18), inset 0 0 0 1px rgba(255,255,255,0.06)" }}
    >
      <img
        src={url}
        alt={`Doxx Card for ${card.username}`}
        className="absolute inset-0 w-full h-full object-cover z-[1]"
      />
      {/* DIVINE — aqueous ripple */}
      <div className="vc-divine-ripple" aria-hidden />
      {/* MYTHIC — starfield + nebula drift */}
      <div className="vc-mythic-cosmos" aria-hidden />
      {/* CELESTIAL — prismatic cracked-glass shards */}
      <div className="vc-celestial-shards" aria-hidden />
      {/* DOXX HALO — a soft white god-rays sweep unique to the Doxx Card,
          fired ONCE on mount via animation-iteration-count. */}
      <div className="vc-doxx-rays" aria-hidden />
      {/* Rarity stripe */}
      <div className="absolute top-2 left-2 right-2 flex items-center justify-between font-mono text-[9px] tracking-[0.3em] uppercase text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.9)] z-[6]">
        <span>CLASSIFIED · MASTER</span>
        <span>#{card.edition_number}</span>
      </div>
      {/* Footer */}
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/95 via-black/70 to-transparent p-3 z-[6]">
        <div className="font-heading uppercase tracking-tight text-white text-lg leading-none">
          {card.username}
        </div>
        <div className="font-mono text-[8px] tracking-[0.3em] text-[#ff3b30] mt-1">
          DOXX CARD · ONE OF ONE
        </div>
      </div>
    </div>
  );
}

function MintModal({ prompt, setPrompt, confirmed, setConfirmed, submitting, onClose, onSubmit, username }) {
  return (
    <div
      className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="doxx-mint-modal"
    >
      <div
        className="relative w-full max-w-2xl border border-[#ff3b30]/40 bg-[#0a0a0a] p-6 sm:p-8 my-8"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          data-testid="doxx-mint-close"
          className="absolute top-3 right-3 text-zinc-500 hover:text-white"
        >
          <X size={18} />
        </button>

        <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.3em] text-[#ff3b30] mb-2">
          <Lock size={12} /> MINT CLASSIFIED · ONE-TIME ONLY
        </div>
        <h3 className="font-heading uppercase tracking-tight text-white text-2xl sm:text-3xl mb-3">
          Describe Your Card
        </h3>
        <p className="text-zinc-400 text-sm leading-relaxed mb-4">
          Whatever you type below becomes your <span className="text-white">permanent</span> Classified
          card art — generated by Gemini Nano Banana with V. Jones Vault&apos;s
          brutalist art direction. Card will be issued as
          {" "}<span className="text-[#ff3b30] font-mono">{username}</span>.
        </p>

        <textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder="A hooded archivist standing in a dim hall of locked filing cabinets, only their eyes visible, holding a single redacted file under a flickering bulb…"
          rows={6}
          maxLength={4000}
          data-testid="doxx-mint-prompt"
          className="w-full px-3 py-3 bg-[#0F0F0F] border border-white/15 focus:border-[#ff3b30] text-white text-sm outline-none resize-y placeholder-zinc-700 font-mono leading-relaxed"
        />
        <div className="font-mono text-[10px] tracking-[0.2em] text-zinc-500 mt-1 text-right">
          {prompt.length} / 4000
        </div>

        <label
          className="flex items-start gap-3 mt-4 cursor-pointer select-none"
          data-testid="doxx-mint-confirm-label"
        >
          <input
            type="checkbox"
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
            className="mt-1 accent-[#ff3b30]"
            data-testid="doxx-mint-confirm-checkbox"
          />
          <span className="text-sm text-zinc-300 leading-relaxed">
            <AlertTriangle size={14} className="inline-block text-[#ff3b30] mr-1 -mt-0.5" />
            I understand this mint is <span className="text-[#ff3b30]">permanent</span>.
            One Doxx Card per account, forever. Whatever I type above is what
            the AI will paint — there is no re-roll, no re-mint, no refund.
          </span>
        </label>

        <div className="flex flex-wrap items-center gap-3 mt-6 pt-4 border-t border-white/10">
          <button
            onClick={onClose}
            data-testid="doxx-mint-cancel"
            className="px-4 py-2.5 border border-white/15 hover:border-white/30 text-zinc-300 font-mono text-[11px] tracking-[0.25em] uppercase transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onSubmit}
            disabled={submitting || !confirmed || prompt.trim().length < 4}
            data-testid="doxx-mint-submit"
            className="ml-auto inline-flex items-center gap-2 px-6 py-2.5 bg-[#ff3b30] hover:bg-white disabled:opacity-40 disabled:hover:bg-[#ff3b30] text-white hover:text-[#050505] disabled:hover:text-white font-mono text-[11px] tracking-[0.3em] uppercase transition-colors"
          >
            {submitting ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
            {submitting ? "Painting your card…" : "Mint forever"}
          </button>
        </div>
      </div>
    </div>
  );
}
